import React, { useState, useEffect } from 'react';
import { Calendar, Clock, ArrowRight, X, Sun, Droplets, Heart, Leaf, Loader, Eye, MessageCircle, Share2, Tag, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { COACH_CONFIG } from '../coachconfig';

const BlogPage = () => {
  const [selectedPost, setSelectedPost] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();

  // Coach ID from config
  const { coachUserId, apiBaseUrl } = COACH_CONFIG;

  // Fetch blog posts from API
  useEffect(() => {
    const fetchBlogPosts = async () => {
      setLoading(true);
      setError('');
      
      try {
        const response = await fetch(`${apiBaseUrl}/blog?coachUserId=${coachUserId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        const result = await response.json();

        if (response.ok && result.success) {
          const posts = result.blogPosts || [];
          // Only show published posts
          const publishedPosts = posts.filter(post => post.status === 'published');
          setBlogPosts(publishedPosts);
          setFilteredPosts(publishedPosts);
        } else {
          console.error('Error fetching blog posts:', result.error);
          setError(result.error || 'Failed to load blog posts');
          setBlogPosts([]);
          setFilteredPosts([]);
        }
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts. Please check your connection and try again.');
        setBlogPosts([]);
        setFilteredPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, [coachUserId, apiBaseUrl]);

  // Filter posts based on category
  useEffect(() => {
    let filtered = blogPosts;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    setFilteredPosts(filtered);
  }, [blogPosts, selectedCategory]);

  // Get unique categories from published posts
  const categories = ['all', ...new Set(blogPosts.map(post => post.category).filter(Boolean))];

  // Helper function to render icon based on icon string or default
  const renderIcon = (iconString) => {
    // Map common icon strings to Lucide icons
    const iconMap = {
      'sun': <Sun className="text-green-600" size={20} />,
      'droplets': <Droplets className="text-green-600" size={20} />,
      'heart': <Heart className="text-green-600" size={20} />,
      'leaf': <Leaf className="text-green-600" size={20} />,
    };

    // If it's a known icon string, return the component
    if (iconString && iconMap[iconString.toLowerCase()]) {
      return iconMap[iconString.toLowerCase()];
    }

    // If it's an emoji, return it as text
    if (iconString && /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(iconString)) {
      return <span className="text-2xl">{iconString}</span>;
    }

    // Default icon
    return <Sun className="text-green-600" size={20} />;
  };

  // Handle navigation to blog details with post data
  const handlePostClick = (post) => {
    navigate(`/blog/${post.id}`, { state: { post } });
  };

  // Format category name for display
  const formatCategory = (category) => {
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-[#f6ffe6] to-[#c4ffa0]/30">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-20 sm:py-24 md:py-32 bg-gradient-to-br from-[#eaffd0] to-[#c4ffa0]/40">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-[#2d4a1e]">
              Kolanut AI <span className="text-green-600">Blog</span>
            </h1>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
              Insights & tips on everything Kolanut AI: marketing, ads, sales, and more.
            </p>
          </div>
        </section>

        {/* Category Filter Section */}
        {!loading && blogPosts.length > 0 && categories.length > 1 && (
          <section className="py-8 border-b border-green-100 bg-[#f6ffe6]">
            <div className="max-w-6xl mx-auto px-4">
              <div className="flex flex-wrap gap-4 justify-center">
                {/* Category Filter */}
                <div className="flex items-center gap-2">
                  <Tag size={16} className="text-green-600" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="bg-white border border-green-200 text-gray-900 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-400 focus:border-transparent"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : formatCategory(category)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Blog Posts Section */}
        <section className="py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-4">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <Loader className="w-8 h-8 animate-spin text-green-600 mx-auto mb-4" />
                  <p className="text-gray-600">Loading blog posts...</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md mx-auto">
                  <p className="text-red-600 mb-4">{error}</p>
                  <button 
                    onClick={() => window.location.reload()} 
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-16">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 max-w-md mx-auto">
                  <p className="text-gray-600 mb-2">
                    {blogPosts.length === 0 
                      ? 'No blog posts available at the moment.' 
                      : 'No posts match your current category filter.'
                    }
                  </p>
                  <p className="text-sm text-gray-500">
                    {blogPosts.length === 0 
                      ? 'Check back soon for new wellness insights!' 
                      : 'Try selecting a different category to see more posts.'
                    }
                  </p>
                  {blogPosts.length > 0 && (
                    <button 
                      onClick={() => setSelectedCategory('all')}
                      className="mt-4 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    >
                      Show All Categories
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post) => (
                  <div 
                    key={post.id}
                    className="bg-white border border-green-100 rounded-2xl overflow-hidden hover:border-green-300 hover:shadow-lg transition-all duration-300 cursor-pointer"
                    onClick={() => handlePostClick(post)}
                  >
                    <div className="relative h-48 w-full">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/success1.webp'; // Fallback image
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-green-200/40 to-transparent"></div>
                      
                      {/* Category Badge */}
                      {post.category && (
                        <div className="absolute top-3 right-3">
                          <span className="bg-green-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
                            {formatCategory(post.category)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar size={14} />
                          <span>{post.date}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock size={14} />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        {renderIcon(post.icon)}
                        <h3 className="text-xl font-semibold text-[#2d4a1e]">{post.title}</h3>
                      </div>
                      <p className="text-gray-700 mb-4 line-clamp-3">{post.excerpt}</p>
                      
                      {/* Author */}
                      {post.author && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                          <User size={14} />
                          <span>{post.author}</span>
                        </div>
                      )}

                      {/* Engagement Stats */}
                      {/* <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                        <div className="flex items-center gap-1">
                          <Eye size={12} />
                          <span>{post.views || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageCircle size={12} />
                          <span>{post.comments || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Share2 size={12} />
                          <span>{post.shares || 0}</span>
                        </div>
                      </div> */}

                      {/* Tags */}
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {post.tags.slice(0, 3).map((tag, index) => (
                            <span 
                              key={index}
                              className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                          {post.tags.length > 3 && (
                            <span className="text-gray-500 text-xs">
                              +{post.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}

                      <button className="text-green-600 hover:text-green-700 font-medium flex items-center gap-2 transition-colors duration-300">
                        Read More <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Modal for full blog post */}
        {selectedPost && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white border border-green-200 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-[#2d4a1e] mb-2">{selectedPost.title}</h2>
                    <div className="flex space-x-4 text-sm text-gray-500">
                      <span>{selectedPost.date}</span>
                      <span>{selectedPost.readTime}</span>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedPost(null)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className="prose max-w-none">
                  {selectedPost.content.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="mb-4 text-gray-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage; 